import { styled } from '@linaria/react'
import React, { type FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from '../styles/colors'
import { TitleAndSubtitle } from '../ui/TitleAndSubtitle'
import TonIcon from '../svgr/TonIcon'
import { Typography } from '../ui/Typography'
import { Tabs } from '../ui/Tabs'
import { useBonusStats } from '../hooks/useBonusData'
import { formatTon } from '../utils/formatTon'
import { BonusTasksTab } from '../components/BonusTasksTab'
import { BonusReferralsTab } from '../components/BonusReferralsTab'
import { Loader } from '../ui/Loader'
import { useAppContext } from '../contexts/App.context'
import { WalletConnectionRequiredDialog } from '../dialogs/WalletConnectionRequiredDialog'
import { BonusWithdrawalCard } from '../components/BonusWithdrawalCard'
import { BonusTasksPreview } from '../components/BonusTasksPreview'

export const bonusTasksPagePath = '/bonuses/tasks'
export const bonusReferralsPagePath = '/bonuses/referrals'

export enum BonusTab {
  Tasks = 'tasks',
  Referrals = 'referrals',
}

export const BonusPage: FC<{ activeTab: BonusTab }> = ({ activeTab }) => {
  const { authenticated, canFetchUserData } = useAppContext()
  const { data: stats, isLoading, error } = useBonusStats()

  if (!authenticated) {
    return (
      <Container>
        <TitleAndSubtitle
          title={<FormattedMessage id="bonus.title" defaultMessage="Бонусы" />}
          textAlign="center"
        />

        <BonusTasksPreview />

        <WalletRequired />
      </Container>
    )
  }

  return (
    <Container>
      <TitleAndSubtitle
        title={<FormattedMessage id="bonus.title" defaultMessage="Бонусы" />}
        textAlign="center"
      />

      {isLoading && (
        <StatsContainer>
          <StatCard>
            <StatValue fontSize={13} fontWeight={700}>
              <SmallLoader /> <TonIcon />
            </StatValue>

            <Typography fontSize={10} color={colors.textSecondary}>
              <FormattedMessage id="bonus.earned" defaultMessage="Заработано" />
            </Typography>
          </StatCard>

          <StatCard>
            <StatValue fontSize={13} fontWeight={700}>
              <SmallLoader /> <TonIcon />
            </StatValue>

            <Typography fontSize={10} color={colors.textSecondary}>
              <FormattedMessage
                id="bonus.canWithdraw"
                defaultMessage="Можно вывести"
              />
            </Typography>
          </StatCard>
        </StatsContainer>
      )}

      {canFetchUserData && stats && !isLoading && !error && (
        <StatsContainer>
          <StatCard>
            <StatValue fontSize={13} fontWeight={700}>
              {formatTon(stats.totalEarned, 2)} <TonIcon />
            </StatValue>

            <Typography fontSize={10} color={colors.textSecondary}>
              <FormattedMessage id="bonus.earned" defaultMessage="Заработано" />
            </Typography>
          </StatCard>

          <BonusWithdrawalCard stats={stats} />
        </StatsContainer>
      )}

      <Tabs
        activeTabId={activeTab}
        tabs={[
          {
            id: BonusTab.Tasks,
            label: (
              <FormattedMessage id="bonus.tasks" defaultMessage="Задания" />
            ),
            to: bonusTasksPagePath,
          },

          {
            id: BonusTab.Referrals,
            label: (
              <FormattedMessage
                id="bonus.referrals"
                defaultMessage="Рефералы"
              />
            ),
            to: bonusReferralsPagePath,
          },
        ]}
      />

      {activeTab === BonusTab.Tasks && <BonusTasksTab />}
      {activeTab === BonusTab.Referrals && <BonusReferralsTab />}
    </Container>
  )
}

const Container = styled.div`
  display: grid;
  gap: 16px;
  padding-top: 12px;
  width: 100%;
  flex: 1;
  background: ${colors.surfacePrimary};
  align-content: flex-start;
`

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
`

const StatCard = styled.div`
  display: grid;
  gap: 2px;
  background: ${colors.surfaceSecondary};
  border-radius: 16px;
  padding: 9px 16px;
  align-content: center;
`

const StatValue = styled(Typography)`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
`

const SmallLoader = styled(Loader)`
  height: 16px;
  width: 16px;
`
const WalletRequired = styled(WalletConnectionRequiredDialog)`
  justify-self: center;
`
